import SEO from '../components/SEO';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiClock, FiUser, FiArrowRight, FiTag } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { fetchAllBlogs, getImageSrc, formatMarathiDate } from '../apiService';
import { useLanguage } from '../utils/LanguageContext';

const Blogs = () => {
  const { language } = useLanguage();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      const blogsData = await fetchAllBlogs();
      setBlogs(blogsData);
    } catch (err) {
      console.error('Error loading blogs:', err);
      setError('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return language === 'MR' 
      ? date.toLocaleDateString('mr-IN', { month: 'short', day: 'numeric' })
      : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (Array.isArray(text)) {
      const fullText = text.join(' ');
      return fullText.length > maxLength ? fullText.substring(0, maxLength) + '...' : fullText;
    }
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const isNewBlog = (dateString) => {
    const blogDate = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - blogDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  const BlogCard = ({ blog, index }) => (
    <motion.div
      className="bg-white border-2 border-red-100 rounded-xl overflow-hidden shadow-md hover:shadow-xl hover:border-red-300 transition-all duration-300 cursor-pointer group relative"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -6, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {isNewBlog(blog.date) && (
        <div className="absolute top-3 right-3 z-10">
          <span className="bg-red-800 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-md">
            {language === 'MR' ? 'नवीन' : 'New'}
          </span>
        </div>
      )}

      <div className="relative overflow-hidden h-48">
        <img loading="lazy" 
          src={getImageSrc(blog.thumbnail)} 
          alt={blog.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="bg-white text-red-800 px-4 py-2 rounded-full font-semibold flex items-center shadow-md">
            <FiArrowRight className="mr-2 w-4 h-4" />
            {language === 'MR' ? 'वाचा' : 'Read'}
          </div>
        </div>
      </div>

      <div className="p-5">
        {blog.tags && blog.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {blog.tags.slice(0, 2).map((tag, i) => (
              <span 
                key={i}
                className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium"
              >
                <FiTag className="w-2 h-2" />
                {tag}
              </span>
            ))}
          </div>
        )}

        <h3 className="text-lg font-bold text-red-800 mb-3 line-clamp-2 group-hover:text-red-600 transition-colors leading-tight">
          {blog.title}
        </h3>

        {blog.content && blog.content[0] && (
          <p className="text-red-600 text-sm line-clamp-3 leading-relaxed mb-4">
            {truncateText(blog.content, 120)}
          </p>
        )}

        <div className="flex items-center justify-between text-xs text-red-700 mb-4">
          <div className="flex items-center">
            <FiUser className="mr-1 text-red-600 w-3 h-3" />
            <span className="font-medium truncate">{blog.author || 'Admin'}</span>
          </div>
          <div className="flex items-center">
            <FiClock className="mr-1 text-red-600 w-3 h-3" />
            <span>{formatDate(blog.date)}</span>
          </div>
        </div>

        <div className="text-center">
          <Link 
            to={`/blog/${blog.slug || blog._id}`}
            className="inline-flex items-center gap-2 bg-red-800 hover:bg-red-900 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300"
          >
            {language === 'MR' ? 'संपूर्ण अनुदिनी वाचा' : 'Read Full Blog'}
            <FiArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-white pt-20">
      <SEO title="ब्लॉग" description="डॉ. राजेंद्र शेंडे यांचे मराठी ब्लॉग पोस्ट वाचा." canonical="/allblogs" />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-medium text-red-800 mb-4">
            {language === 'MR' ? 'सर्व मराठी अनुदिनी' : 'All Marathi Blogs'}
          </h1>
          <div className="w-24 h-1 bg-red-800 mx-auto mb-4" />
          <p className="text-lg text-red-700 max-w-2xl mx-auto">
            {language === 'MR' 
              ? 'आमच्या संपूर्ण मराठी अनुदिनी संग्रहातून तुमच्या आवडत्या विषयावर वाचा'
              : 'Read about your favorite topics from our complete Marathi blog collection'}
          </p>
        </motion.div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(12)].map((_, index) => (
              <div key={index} className="bg-white border-2 border-red-100 rounded-xl shadow-md overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-300"></div>
                <div className="p-5">
                  <div className="h-4 bg-gray-300 rounded mb-3"></div>
                  <div className="h-4 bg-gray-300 rounded mb-3 w-3/4"></div>
                  <div className="h-3 bg-gray-300 rounded mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-12">
            <div className="bg-red-50 border-2 border-red-100 rounded-xl p-8 max-w-md mx-auto">
              <div className="text-6xl text-red-800 mb-4">⚠</div>
              <p className="text-red-800 font-semibold mb-4">
                {language === 'MR' ? 'ब्लॉग लोड करताना त्रुटी' : 'Error loading blogs'}
              </p>
              <button 
                onClick={loadBlogs}
                className="bg-red-800 hover:bg-red-900 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                {language === 'MR' ? 'पुन्हा प्रयत्न करा' : 'Try Again'}
              </button>
            </div>
          </div>
        )}

        {!loading && !error && blogs.length === 0 && (
          <motion.div 
            className="text-center py-12"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-2xl font-bold text-red-800 mb-2">
              {language === 'MR' ? 'कोणतेही ब्लॉग सापडले नाहीत' : 'No Blogs Found'}
            </h3>
            <p className="text-red-600">
              {language === 'MR' ? 'सध्या कोणतेही ब्लॉग उपलब्ध नाहीत' : 'No blogs are currently available'}
            </p>
          </motion.div>
        )}

        {!loading && !error && blogs.length > 0 && (
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {blogs.map((blog, index) => (
              <BlogCard key={blog._id} blog={blog} index={index} />
            ))}
          </motion.div>
        )}

        {!loading && !error && blogs.length > 0 && (
          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <p className="text-red-700 font-medium">
              {language === 'MR' 
                ? `एकूण ${blogs.length} अनुदिनी दाखवत आहे`
                : `Showing ${blogs.length} blogs`}
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Blogs;